import { generateAccessName, AccessKey } from "../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_CHAT,
];
const ACCESS_SECTION_FILTER: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_CHAT,
	AccessKey.FILTER,
];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	FILTER = AccessKey.FILTER,
	RELEVANCE = AccessKey.RELEVANCE,
	CHOISE_CUSTOMERS = AccessKey.CHOISE_CUSTOMERS,
	SEND_SMS = AccessKey.SEND_SMS,
	REPLY_TO_A_MESSAGE = AccessKey.REPLY_TO_A_MESSAGE,
	//
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	EXECUTORS = AccessKey.EXECUTORS,
	CAR_PARKS = AccessKey.CAR_PARKS,
	EXECUTOR_TEAMS = AccessKey.EXECUTOR_TEAMS,
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];
const generateAccessSectionFilter = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_FILTER,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.FILTER]: generateAccessSection(AccessKey.FILTER),
	[TabKeys.RELEVANCE]: generateAccessSection(AccessKey.RELEVANCE),
	[TabKeys.CHOISE_CUSTOMERS]: generateAccessSection(
		AccessKey.CHOISE_CUSTOMERS,
	),
	[TabKeys.SEND_SMS]: generateAccessSection(AccessKey.SEND_SMS),
	[TabKeys.REPLY_TO_A_MESSAGE]: generateAccessSection(
		AccessKey.REPLY_TO_A_MESSAGE,
	),
	//
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]: generateAccessSectionFilter(
		AccessKey.COMPANIES_AND_TAXI_SERVICES,
	),
	[TabKeys.EXECUTORS]: generateAccessSectionFilter(AccessKey.EXECUTORS),
	[TabKeys.CAR_PARKS]: generateAccessSectionFilter(AccessKey.CAR_PARKS),
	[TabKeys.EXECUTOR_TEAMS]: generateAccessSectionFilter(
		AccessKey.EXECUTOR_TEAMS,
	),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
